import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronDownIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { getAvailableCountries } from '../../utils/holidayUtils';

const CountrySelector = ({ selectedCountry, onCountryChange, className = '' }) => {
  const [countries, setCountries] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      setLoading(true);
      const availableCountries = await getAvailableCountries();
      setCountries(availableCountries);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCountrySelect = (countryCode) => {
    onCountryChange(countryCode);
    setIsOpen(false);
  };

  const selectedCountryName = countries.find(c => c.countryCode === selectedCountry)?.name || selectedCountry;

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
        disabled={loading}
      >
        <GlobeAltIcon className="w-4 h-4 text-gray-500" />
        <span className="text-sm font-medium text-gray-700">
          {loading ? 'Loading...' : selectedCountryName}
        </span>
        <ChevronDownIcon className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-60 overflow-y-auto"
          >
            {countries.map((country) => (
              <button
                key={country.countryCode}
                onClick={() => handleCountrySelect(country.countryCode)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                  selectedCountry === country.countryCode 
                    ? 'bg-blue-50 text-blue-700 font-medium' 
                    : 'text-gray-700'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span>{country.name}</span>
                  <span className="text-xs text-gray-500 font-mono">{country.countryCode}</span>
                </div>
              </button>
            ))}
          </motion.div>
        </>
      )}
    </div>
  );
};

export default CountrySelector;